import { useState, type FormEvent } from "react";
import { useListDocuments, useCreateDocument, useDeleteDocument, getListDocumentsQueryKey, useListCompanies } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import { Plus, FileText, Shield, Trash2 } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";

const categoryLabels: Record<string, string> = {
  pitch_deck: "Pitch Deck",
  financials: "Financials",
  cap_table: "Cap Table",
  legal: "Legal",
  other: "Other",
};

export default function Documents({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: documents, isLoading } = useListDocuments({
    category: categoryFilter !== "all" ? categoryFilter as "pitch_deck" | "financials" | "cap_table" | "legal" | "other" : undefined,
  });
  const { data: companies } = useListCompanies();
  const createMutation = useCreateDocument();
  const deleteMutation = useDeleteDocument();

  const companyMap = new Map((companies ?? []).map(c => [c.id, c.name]));

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate(
      {
        data: {
          companyId: parseInt(form.get("companyId") as string, 10),
          title: form.get("title") as string,
          category: form.get("category") as "pitch_deck" | "financials" | "cap_table" | "legal" | "other",
          uploadedBy: (form.get("uploadedBy") as string) || null,
          visibleToInvestors: form.get("visibleToInvestors") === "on",
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListDocumentsQueryKey() });
          setDialogOpen(false);
          toast({ title: "Document added" });
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListDocumentsQueryKey() });
          toast({ title: "Document deleted" });
        },
      }
    );
  };

  return (
    <div className="space-y-6" data-testid="page-documents">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Document Vault</h1>
            <InfoTip content="A central repository for all portfolio documents — pitch decks, financials, legal agreements, reports, and more. Documents tagged as 'approved for investors' will appear in the relevant company's investor microsite. Use categories and company filters to find what you need." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">All documents across portfolio companies</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-document"><Plus className="w-4 h-4 mr-2" />Add Document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">Add Document</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <select name="companyId" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-doc-company">
                  <option value="">Select company</option>
                  {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label>Title</Label><Input name="title" required data-testid="input-doc-title" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select name="category" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-doc-category">
                    <option value="pitch_deck">Pitch Deck</option>
                    <option value="financials">Financials</option>
                    <option value="cap_table">Cap Table</option>
                    <option value="legal">Legal</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-2"><Label>Uploaded By</Label><Input name="uploadedBy" data-testid="input-doc-uploaded-by" /></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="visibleToInvestors" id="visibleToInvestors" className="rounded" data-testid="checkbox-doc-visible" />
                <Label htmlFor="visibleToInvestors" className="text-sm">Visible to investors</Label>
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending} data-testid="button-submit-document">
                {createMutation.isPending ? "Adding..." : "Add Document"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
        <SelectTrigger className="w-48" data-testid="select-filter-doc-category">
          <SelectValue placeholder="All categories" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All categories</SelectItem>
          <SelectItem value="pitch_deck">Pitch Deck</SelectItem>
          <SelectItem value="financials">Financials</SelectItem>
          <SelectItem value="cap_table">Cap Table</SelectItem>
          <SelectItem value="legal">Legal</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : documents && documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <Card key={doc.id} data-testid={`card-doc-${doc.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{doc.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${doc.companyId}` : `/companies/${doc.companyId}`}>
                        <span className="text-xs text-primary hover:underline cursor-pointer">{companyMap.get(doc.companyId) ?? `#${doc.companyId}`}</span>
                      </Link>
                      <Badge variant="outline" className="text-xs">{categoryLabels[doc.category] ?? doc.category}</Badge>
                      {doc.uploadedBy && <span className="text-xs text-muted-foreground">by {doc.uploadedBy}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {doc.visibleToInvestors && <Badge variant="outline" className="text-xs gap-1"><Shield className="w-3 h-3" />Investor</Badge>}
                  <span className="text-xs text-muted-foreground">{new Date(doc.createdAt).toLocaleDateString()}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} data-testid={`button-delete-doc-${doc.id}`}>
                    <Trash2 className="w-3 h-3 text-muted-foreground" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No documents found</p>
            <p className="text-sm text-muted-foreground mt-1">{categoryFilter !== "all" ? "Try changing the filter" : "Add your first document"}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
